import { useMountEffect } from 'primereact/hooks';
import { classNames } from 'primereact/utils';
import React, { useContext, useRef } from 'react';
import { AppTopbarRef } from 'src/types';
import { LayoutContext } from '../context/LayoutContext';
import AppFooter from './AppFooter';
import AppTopbar from './AppTopbar';
import AppRouter from 'src/app/routes/AppRouter';

interface AppTopbarProps {
  onNavClick?: (id: string) => void; // optional callback
}

const PublicLayout: React.FC = () => {
  const { layoutState, layoutConfig, setLayoutConfig, setLayoutState } =
    useContext(LayoutContext);
  const topbarRef = useRef<AppTopbarRef>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const hideMenu = () => {
    setLayoutState((prevLayoutState) => ({
      ...prevLayoutState,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }));
    unblockBodyScroll();
  };

  const hideProfileMenu = () => {
    setLayoutState((prevLayoutState) => ({
      ...prevLayoutState,
      profileSidebarVisible: false,
    }));
  };

  const unblockBodyScroll = () => {
    document.body.classList.remove('blocked-scroll');
  };

  // Effect to bind/unbind menu click outside listener
  useMountEffect(() => {
    const bindMenuOutsideClickListener = (event: MouseEvent) => {
      const isOutsideClicked = !(
        sidebarRef.current?.contains(event.target as Node) ||
        topbarRef.current?.menubutton?.contains(event.target as Node)
      );

      if (isOutsideClicked) {
        hideMenu();
      }
    };

    if (layoutState.overlayMenuActive || layoutState.staticMenuMobileActive) {
      document.addEventListener('click', bindMenuOutsideClickListener);
    }

    return () => {
      document.removeEventListener('click', bindMenuOutsideClickListener);
    };
    // TODO : Check with Next.js old code
    // }, [layoutState.overlayMenuActive, layoutState.staticMenuMobileActive]);
  });

  // Effect to bind/unbind profile menu click outside listener
  useMountEffect(() => {
    const bindProfileMenuOutsideClickListener = (event: MouseEvent) => {
      const isOutsideClicked = !(
        topbarRef.current?.topbarmenu?.contains(event.target as Node) ||
        topbarRef.current?.topbarmenubutton?.contains(event.target as Node)
      );

      if (isOutsideClicked) {
        hideProfileMenu();
      }
    };

    if (layoutState.profileSidebarVisible) {
      document.addEventListener('click', bindProfileMenuOutsideClickListener);
    }

    return () => {
      document.removeEventListener(
        'click',
        bindProfileMenuOutsideClickListener
      );
    };
    // TODO : Check with Next.js old code
    // }, [layoutState.profileSidebarVisible]);
  });

  const containerClass = classNames('layout-wrapper', {
    'layout-overlay': layoutConfig.menuMode === 'overlay',
    'layout-static': layoutConfig.menuMode === 'static',
    'layout-static-inactive':
      layoutState.staticMenuDesktopInactive &&
      layoutConfig.menuMode === 'static',
    'layout-overlay-active': layoutState.overlayMenuActive,
    'layout-mobile-active': layoutState.staticMenuMobileActive,
    'p-input-filled': layoutConfig.inputStyle === 'filled',
    'p-ripple-disabled': !layoutConfig.ripple,
  });

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
     <div className={containerClass}>
      <AppTopbar ref={topbarRef} />
      <div className="layout-full-container">
        <div className="layout-full">
          <AppRouter />
        </div>
      </div>
      <AppFooter />
      {/* <AppConfig /> */}
      <div className="layout-mask"></div>
    </div>
  );
};

export default PublicLayout;
